import Big from 'big.js'
import { writable } from 'svelte/store'

import type { DocumentItemTaxTotal } from '$lib/components/document/doc_types'
import type { DocShippingItem } from '$lib/stores/doc_shipping_items/types'
import type { Estimate, EstimateTax } from '$lib/stores/estimate/types'
import { CalculationType } from '$lib/types'

import { makeEstimate } from './factories'

type EstimateStore = {
	estimateInfo: Estimate
}

const store = writable({estimateInfo: makeEstimate({})} as EstimateStore)
export { store as estimate }
export let last_saved_at = 0

export function setLastSavedAt(savedAt: number) {
	last_saved_at = savedAt
}

// ================================
// CALCULATE
function add(a: Big, b: Big) {
	return a.plus(b)
}

export const calculate = (estimate: Estimate): Estimate => {
	// let doc = { ...estimate }
	const doc = estimate

	// // calculate item.total
	// e.items = calculateItems(e.items, e.discounts, e.taxes)

	// // sum items.total
	// e.subtotal = e.items.reduce((a: Big, i: DocumentItem) => a.plus(i.subtotal), Big(0))

	// //console.log("subtotal: ", e.subtotal.valueOf())

	// // calculate discount.total
	// e.discounts = calculateDiscounts(e.discounts, e.subtotal)

	// // sum discounts.total
	// e.discount = e.discounts.reduce((a: Big, d: EstimateDiscount) => a.plus(d.total), Big(0))

	// // calculate taxes.total
	// e.taxes = calculateTaxes(e.items, e.taxes, e.discounts)

	// // sum taxes.total
	// e.tax = e.taxes.reduce((a: Big, t: EstimateTax) => a.plus(t.total), Big(0))

	// // calculate grandtotal
	// e.grand_total = e.subtotal.minus(e.discount).plus(e.tax)

	// Subtotal is required early to 'scale' fixed discounts onto items
	const subtotal_items = doc.items
		// note that quantity is not a Big it is a numeric, so quantity.times(...)
		// would be a mistake
		.map(({ price, quantity }) => price.times(quantity).round(2))
		.reduce(add, Big(0))

	// Subtotal is required early to 'scale' fixed discounts onto items
	const subtotal_shipping_items = doc.shipping_items
		// note that quantity is not a Big it is a numeric, so quantity.times(...)
		// would be a mistake
		.map(({ price }) => price.round(2))
		.reduce(add, Big(0))

	const subtotal = subtotal_items.add(subtotal_shipping_items)

	// Lookup table for items to calculate tax
	const tax_lookup: Record<string, Big> = {}
	console.log('div taxes:', JSON.stringify(doc.taxes))
	doc.taxes.forEach((tax) => {
		console.log('div:', tax.rate)
		if (tax.rate) tax_lookup[tax.id] = tax.rate.div(100)
	})

	// Generate decorated items
	const items = doc.items.map((item) => {
		const item_subtotal = item.price.times(item.quantity).round(2)

		// now apply discounts that are pre-tax to this item
		const pre_tax_discount = doc.discounts
			.map((discount) => {
				if (discount.type === CalculationType.Rate) {
					//console.log("discount ----> ", discount.rate.div(100).times(item_subtotal).round(2).toString())
					return discount.rate.div(100).times(item_subtotal).round(2)
				} else if (discount.type === CalculationType.Fixed) {
					// *scale* discount to this item from document subtotal
					return subtotal.eq(0)
						? Big(0)
						: discount.fixed.div(subtotal).times(item_subtotal).round(2)
				} else {
					throw new Error(`Unknown discount calculation type: ${discount.type}`)
				}
			})
			.reduce(add, Big(0))

		//console.log("pre_tax_discount ----> ", pre_tax_discount.toString())

		// total after pre tax discounts
		const pre_tax_total = item_subtotal.minus(pre_tax_discount)

		// now apply taxes
		let total_tax = Big(0)
		const tax_totals: DocumentItemTaxTotal = {}
		item.taxes.forEach((tax_id) => {
			const tax = pre_tax_total.times(tax_lookup[tax_id]).round(2)
			tax_totals[tax_id] = tax
			//console.log("tax ----> ", tax.toString())
			total_tax = total_tax.plus(tax)
		})

		//console.log("total_tax ----> ", total_tax.toString())

		// total could be misleading here, so watch out
		// pre AND post tax discounts still have to be applied globally
		// even though pre tax discounts were used to find total tax
		const total = pre_tax_total.plus(total_tax)

		return {
			...item,
			subtotal: item_subtotal,
			tax_totals: tax_totals,
			tax: total_tax,
			total: total
		}
	})

	// Generate decorated shipping_items
	const shipping_items = doc.shipping_items.map((item): DocShippingItem => {
		// const item_subtotal = item.price.round(2)
		const item_subtotal = Big(item.price).round(2)

		// now apply discounts that are pre-tax to this item
		const pre_tax_discount = doc.discounts
			.map((discount) => {
				if (discount.type === CalculationType.Rate) {
					return discount.rate.div(100).times(item_subtotal).round(2)
				} else if (discount.type === CalculationType.Fixed) {
					// *scale* discount to this item from invoice subtotal
					return subtotal.eq(0)
						? Big(0)
						: discount.fixed.div(subtotal).times(item_subtotal).round(2)
				} else {
					throw new Error(`Unknown discount calculation type: ${discount.type}`)
				}
			})
			.reduce(add, Big(0))

		// total after pre tax discounts
		const pre_tax_total = item_subtotal.minus(pre_tax_discount)

		// now apply taxes
		let total_tax = Big(0)
		const tax_totals: DocumentItemTaxTotal = {}
		item.taxes.forEach((tax_id) => {
			const tax = pre_tax_total.times(tax_lookup[tax_id]).round(2)
			tax_totals[tax_id] = tax
			total_tax = total_tax.plus(tax)
		})

		// total could be misleading here, so watch out
		// pre AND post tax discounts still have to be applied globally
		// even though pre tax discounts were used to find total tax
		const total = pre_tax_total.plus(total_tax)

		return {
			...item,
			subtotal: item_subtotal,
			tax: total_tax,
			tax_totals,
			total
		}
	})

	const discounts = doc.discounts.map((discount) => {
		if (discount.type === CalculationType.Rate) {
			return {
				...discount,
				total: discount.rate.div(100).times(subtotal).round(2)
			}
		} else if (discount.type === CalculationType.Fixed) {
			return {
				...discount,
				total: discount.fixed
			}
		} else {
			throw new Error(`Unknown discount calculation type: ${discount.type}`)
		}
	})

	const discount = discounts.map(({ total }) => total).reduce(add, Big(0))
	const pre_tax_total = subtotal.minus(discount)

	const post_tax_total_items = items.map(({ total }) => total).reduce(add, Big(0))
	const post_tax_total_shippings = shipping_items.map(({ total }) => total).reduce(add, Big(0))

	let post_tax_total = post_tax_total_items.add(post_tax_total_shippings)
	if (post_tax_total.eq(0)) {
		post_tax_total = pre_tax_total
	}

	// const taxes = doc.taxes.map((tax) => ({
	// 	...tax,
	// 	total: items.map(({ tax_totals }) => tax_totals[tax.id] || Big(0)).reduce(add, Big(0))
	// }))
	const taxes = doc.taxes.map((tax) => ({
		...tax,
		total: items.map(({ tax_totals }) => tax_totals[tax.id] || Big(0)).reduce(add, Big(0)).add(
				   shipping_items.map(({ tax_totals }) => tax_totals[tax.id] || Big(0)).reduce(add, Big(0))
		)
	}))
	// const tax = taxes.reduce((a: Big, t: InvoiceTax) => a.plus(t.total), Big(0))
	const tax = taxes.reduce((a: Big, t: EstimateTax) => a.plus(t.total), Big(0))

	// console.log('estimate.calculate: end',doc)
	return {
		...doc,
		items,
		shipping_items,

		subtotal, // basic line items added
		discounts: discounts, // discounts array
		discount: discount, // discount total amount
		taxes: taxes, // taxes array[]
		tax: tax, // taxes total amount

		//pre_tax_total, // after pre tax discounts
		//post_tax_total, // after taxes
		//grand_total, // after post tax discounts
		grand_total: subtotal.minus(discount).plus(tax)

		// deposits,
		//deposit_total, // all deposits (whether they are paid or not!)
		//payment_total,
		// deposit_due,
		// balance
	}
	// return doc
}


